import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './FrontageRun.scss';

/**
 * Collapsed list shows up to this many frontages; 3+ fold into a "+N more"
 * expander so the stack stays short and the postcode never dangles.
 */
const VISIBLE_FRONTAGES = 2;

interface FrontageRunProps {
  /** A building's OTHER street frontages, primary already excluded, e.g.
   *  ["Fredrikinkatu 34", "Narinkka 5"]. */
  lines: string[];
  /** Shared postcode + city, shown ONCE on the first frontage's line (building
   *  view). Omitted on surfaces that already show the city (preview card /
   *  panel). */
  district?: string;
  /** True while the footprint probe is in flight — holds one skeleton row. */
  loading?: boolean;
  /** Host modifier, e.g. "frontage-run--compact" for the preview card / panel. */
  className?: string;
}

/**
 * A corner/multi-frontage building's OTHER street frontages (Figma 2384:38727):
 * one frontage per line, with a SINGLE "⌐" corner mark on the first line (one
 * glyph for the group, not one per name) and the rest aligned beneath it. The
 * shared postcode sits on the first frontage's own line, so it is baseline-
 * aligned with it and never floats mid-column.
 *
 * Handles the 3+ case on mobile and desktop: the stack caps at VISIBLE_FRONTAGES
 * + an in-place "+N more" expander, so the Kamppi 7-frontage block never grows
 * tall. The screen-reader path gets every street (visible names as text + the
 * expander's aria-label).
 */
const FrontageRun: React.FC<FrontageRunProps> = ({
  lines,
  district,
  loading,
  className,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const root = `frontage-run${className ? ` ${className}` : ''}`;

  if (!lines.length && loading) {
    return (
      <div className={root} aria-hidden="true">
        <span className="frontage-run__skeleton" />
      </div>
    );
  }

  const hasFrontages = lines.length > 0;
  if (!hasFrontages && !district) return null;

  const hiddenCount = Math.max(0, lines.length - VISIBLE_FRONTAGES);
  const shown = expanded ? lines : lines.slice(0, VISIBLE_FRONTAGES);

  return (
    <div className={root}>
      {shown.map((line, i) => (
        <div className="frontage-run__row" key={`${i}-${line}`}>
          <span className="frontage-run__name">
            {i === 0 && <span className="frontage-run__corner" aria-hidden="true" />}
            {line}
          </span>
          {i === 0 && district && (
            <span className="frontage-run__district">{district}</span>
          )}
        </div>
      ))}

      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          className="frontage-run__more"
          aria-label={t('building.view.frontagesMoreAria', {
            n: hiddenCount,
            streets: lines.slice(VISIBLE_FRONTAGES).join(', '),
            defaultValue: 'Show {{n}} more: {{streets}}',
          })}
          onClick={() => setExpanded(true)}
        >
          {t('building.view.frontagesMore', { n: hiddenCount, defaultValue: '+{{n}} more' })}
        </button>
      )}
      {expanded && hiddenCount > 0 && (
        <button
          type="button"
          className="frontage-run__more"
          onClick={() => setExpanded(false)}
        >
          {t('building.view.frontagesLess', 'Show less')}
        </button>
      )}

      {!hasFrontages && district && (
        <span className="frontage-run__district">{district}</span>
      )}
    </div>
  );
};

export default FrontageRun;
